import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useEffect } from 'react';

import { Text, SimpleGrid, Box, Flex, Button } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/home/FormInput';
import DateInput from 'components/fields/home/DateInput';
import SelectInput from 'components/fields/home/SelectInput';

import getDateFromString from 'helpers/getDateFromString';
import isValidEmail from 'helpers/isValidEmail';

import {
  changeApplicants,
  removeMissingMandatoryField,
  addAdditionalInsured,
  changeAdditionalInsured,
  removeAdditionalInsured,
} from 'reducers/homePersonalFormReducer';

import getDateString from 'helpers/getDateString';

import formatEmail from 'helpers/formatEmail';

import { SECTIONS } from 'views/home_form/constants';
import { WIP_STATUS_APPLICATION_CREATION, WIP_STATUS_DOCUMENT_GENERATION } from 'variables/constants';

const INTEREST_OPTIONS = [
  { value: '', label: 'Select Interest...' },
  { value: 'None', label: 'None' },
  { value: 'Renter', label: 'Renter' },
  { value: 'Owner', label: 'Owner' },
];

const Applicants = () => {
  const dispatch = useAppDispatch();

  const data = useAppSelector(
    (state) => state.homePersonalForm.data?.applicants || [],
  );

  const applicant = data[0];
  const coApplicant = data[1];

  const additionalInsureds = useAppSelector(
    (state) => state.homePersonalForm.data?.additional_insureds ?? [],
  );
  const wipStatus = useAppSelector((state) => state.homePersonalForm.wip_status);

  // Check if applicant emails match
  const validateApplicantEmails = () => {
    const applicant1Email = applicant?.email?.toLowerCase().trim();
    const applicant2Email = coApplicant?.email?.toLowerCase().trim();

    if (
      applicant1Email &&
      applicant2Email &&
      applicant1Email === applicant2Email
    ) {
      return 'E-signing will not be available if both applicants use the same email';
    }

    return null;
  };

  // Check if a value matches any of the dropdown options (excluding empty option)
  const isValidDropdownValue = (value: string) => {
    return INTEREST_OPTIONS.slice(1).some((option) => option.value === value);
  };

  // Determine if we should show input field instead of dropdown
  const shouldShowInputForInterest = (applicantData: any) => {
    const interest = applicantData?.interest;
    return (
      interest && interest.trim() !== '' && !isValidDropdownValue(interest)
    );
  };

  const onApplicantChange = (event: any, index: number) => {
    const { name, value, checked, type } = event.target;
    let payload = type === 'checkbox' ? checked : value;

    // Format email to remove whitespaces
    if (name === 'email' && type !== 'checkbox') {
      payload = formatEmail(value);
    }

    dispatch(changeApplicants({ index, name, value: payload }));
  };

  const onDateChange = (date: Date, name: string, index: number) => {
    dispatch(changeApplicants({ index, name, value: getDateString(date) }));
  };

  const hasCoApplicant =
    !!coApplicant?.name?.trim() &&
    !!coApplicant?.interest?.trim() &&
    !!coApplicant?.date_of_birth &&
    !!coApplicant?.occupation?.trim();
  const isAdditionalInsuredsEnabled = useFeatureFlag('enable-additional-insureds');
  const canModifyInsureds = [WIP_STATUS_APPLICATION_CREATION, WIP_STATUS_DOCUMENT_GENERATION].includes(wipStatus);
  const isInsuredFilled = (insured: { name?: string }) =>
    !!insured.name?.trim();
  const lastInsuredFilled =
    additionalInsureds.length > 0 &&
    isInsuredFilled(additionalInsureds[additionalInsureds.length - 1]);
  const canAddInsured =
    isAdditionalInsuredsEnabled &&
    canModifyInsureds &&
    hasCoApplicant &&
    additionalInsureds.length < 2 &&
    (additionalInsureds.length === 0 || lastInsuredFilled);

  const onAdditionalInsuredChange = (index: number, event: any) => {
    const { name, value } = event.target;
    const formattedValue = name === 'email' ? formatEmail(value) : value;
    dispatch(changeAdditionalInsured({ index, name, value: formattedValue }));
  };

  const onAdditionalInsuredDateChange = (index: number, date: Date, name: string) => {
    dispatch(changeAdditionalInsured({ index, name, value: getDateString(date) }));
  };

  const validateAdditionalInsuredEmail = (email: string, index: number) => {
    const normalized = email?.toLowerCase().trim();
    if (!normalized) return null;
    const otherEmails = [
      data[0]?.email?.toLowerCase().trim(),
      data[1]?.email?.toLowerCase().trim(),
      ...additionalInsureds
        .filter((_, i) => i !== index)
        .map((ins) => ins.email?.toLowerCase().trim()),
    ].filter(Boolean);
    if (otherEmails.includes(normalized)) {
      return 'E-signing will not be available if additional insured shares the same email as another signer';
    }
    return null;
  };

  useEffect(() => {
    const applicant2 = coApplicant;
    if (!applicant2) return;

    const isApplicant2Empty =
      !applicant2.email &&
      !applicant2.name &&
      !applicant2.date_of_birth &&
      !applicant2.occupation &&
      !applicant2.years_continuously_employed &&
      !applicant2.interest;

    if (isApplicant2Empty) {
      dispatch(removeMissingMandatoryField('Email (Applicant 2)'));
      dispatch(removeMissingMandatoryField('Name (Applicant 2)'));
      dispatch(removeMissingMandatoryField('Date of Birth (Applicant 2)'));
      dispatch(removeMissingMandatoryField('Occupation (Applicant 2)'));
      dispatch(removeMissingMandatoryField('Years Employed (Applicant 2)'));
      dispatch(removeMissingMandatoryField('Interest (Applicant 2)'));
    }
  }, [data, dispatch]);

  if (!data || data.length === 0) return null;

  return (
    <Card mb="20px" id="sectionApplicants">
      <Flex justifyContent="space-between" alignItems="center" mb="20px">
        <Text fontWeight="bold" fontSize="2xl">
          Applicants
        </Text>
        {canAddInsured && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => dispatch(addAdditionalInsured())}
          >
            + Add Additional Insured
          </Button>
        )}
      </Flex>

      <SimpleGrid columns={2} spacing="40px">
        <Box>
          <Text fontWeight="semibold" fontSize="lg" mb="15px">
            Applicant 1
          </Text>

          <SimpleGrid columns={1} spacing="15px">
            <SimpleGrid columns={3} spacing="15px" alignItems="end">
              <Box>
                <FormInput
                  section={SECTIONS.APPLICANTS}
                  label="Email"
                  subLabel="Applicant 1"
                  name="email"
                  value={applicant?.email || ''}
                  onChange={(event: any) => onApplicantChange(event, 0)}
                />
                {!isValidEmail(applicant.email) && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    Please enter a valid email address
                  </Text>
                )}
              </Box>
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Name"
                subLabel="Applicant 1"
                name="name"
                value={applicant?.name || ''}
                onChange={(event: any) => onApplicantChange(event, 0)}
                required
                disabled
              />
              <DateInput
                section={SECTIONS.APPLICANTS}
                label="Date of Birth"
                subLabel="Applicant 1"
                date={getDateFromString(applicant?.date_of_birth)}
                onChange={(date: Date) =>
                  onDateChange(date, 'date_of_birth', 0)
                }
                required
                disabled
              />
            </SimpleGrid>

            <SimpleGrid columns={3} spacing="15px" alignItems="end">
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Occupation"
                subLabel="Applicant 1"
                name="occupation"
                value={applicant?.occupation || ''}
                onChange={(event: any) => onApplicantChange(event, 0)}
                required
              />
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Years Employed"
                subLabel="Applicant 1"
                name="years_continuously_employed"
                value={applicant?.years_continuously_employed || ''}
                onChange={(event: any) => onApplicantChange(event, 0)}
                required={true}
              />
              {shouldShowInputForInterest(applicant) ? (
                // This is for backwards compatibility when this field was a free form text input
                <FormInput
                  section={SECTIONS.APPLICANTS}
                  label="Legal Interest"
                  subLabel="Applicant 1"
                  name="interest"
                  value={applicant?.interest || ''}
                  onChange={(event: any) => onApplicantChange(event, 0)}
                  required={true}
                />
              ) : (
                <SelectInput
                  section={SECTIONS.APPLICANTS}
                  label="Legal Interest"
                  subLabel="Applicant 1"
                  name="interest"
                  value={applicant?.interest || ''}
                  options={INTEREST_OPTIONS}
                  onChange={(event: any) => onApplicantChange(event, 0)}
                  required={true}
                />
              )}
            </SimpleGrid>
          </SimpleGrid>
        </Box>

        <Box>
          <Text fontWeight="semibold" fontSize="lg" mb="15px">
            Applicant 2
          </Text>

          <SimpleGrid columns={1} spacing="15px">
            <SimpleGrid columns={3} spacing="15px">
              <Box>
                <FormInput
                  section={SECTIONS.APPLICANTS}
                  label="Email"
                  subLabel="Applicant 2"
                  name="email"
                  value={coApplicant?.email || ''}
                  onChange={(event: any) => onApplicantChange(event, 1)}
                  required={
                    applicant?.email.length > 0 && coApplicant?.name?.length > 0
                  }
                />
                {validateApplicantEmails() && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {validateApplicantEmails()}
                  </Text>
                )}
                {!isValidEmail(coApplicant.email) && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    Please enter a valid email address
                  </Text>
                )}
              </Box>
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Name"
                subLabel="Applicant 2"
                name="name"
                value={coApplicant?.name || ''}
                onChange={(event: any) => onApplicantChange(event, 1)}
                required={coApplicant?.email?.length > 0}
              />
              <DateInput
                section={SECTIONS.APPLICANTS}
                label="Date of Birth"
                subLabel="Applicant 2"
                date={getDateFromString(coApplicant?.date_of_birth)}
                onChange={(date: Date) =>
                  onDateChange(date, 'date_of_birth', 1)
                }
                required={coApplicant?.name?.length > 0}
                isClearable
              />
            </SimpleGrid>

            <SimpleGrid columns={3} spacing="15px" alignItems="end">
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Occupation"
                subLabel="Applicant 2"
                name="occupation"
                value={coApplicant?.occupation || ''}
                onChange={(event: any) => onApplicantChange(event, 1)}
                required={coApplicant?.name?.length > 0}
              />
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Years Employed"
                subLabel="Applicant 2"
                name="years_continuously_employed"
                value={coApplicant?.years_continuously_employed || ''}
                onChange={(event: any) => onApplicantChange(event, 1)}
                required={coApplicant?.name?.length > 0}
              />
              {shouldShowInputForInterest(coApplicant) ? (
                // This is for backwards compatibility when this field was a free form text input
                <FormInput
                  section={SECTIONS.APPLICANTS}
                  label="Legal Interest"
                  subLabel="Applicant 2"
                  name="interest"
                  value={coApplicant?.interest || ''}
                  onChange={(event: any) => onApplicantChange(event, 1)}
                  required={coApplicant?.name?.length > 0}
                />
              ) : (
                <SelectInput
                  section={SECTIONS.APPLICANTS}
                  label="Legal Interest"
                  subLabel="Applicant 2"
                  name="interest"
                  value={coApplicant?.interest || ''}
                  options={INTEREST_OPTIONS}
                  onChange={(event: any) => onApplicantChange(event, 1)}
                  required={coApplicant?.name?.length > 0}
                />
              )}
            </SimpleGrid>
          </SimpleGrid>
        </Box>
      </SimpleGrid>

      {isAdditionalInsuredsEnabled && additionalInsureds.map((insured, index) => (
        <Box key={index} mt="20px" p="15px" borderWidth="1px" borderRadius="md" borderColor="gray.200">
          <Flex justifyContent="space-between" alignItems="center" mb="10px">
            <Text fontWeight="medium" fontSize="sm" color="gray.600">
              Additional Insured #{index + 1}
            </Text>
            {canModifyInsureds && (
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => dispatch(removeAdditionalInsured(index))}
              >
                Remove
              </Button>
            )}
          </Flex>
          <SimpleGrid columns={5} spacing="15px" alignItems="end">
            <Box>
              <FormInput
                section={SECTIONS.APPLICANTS}
                label="Email"
                name="email"
                value={insured.email || ''}
                onChange={(e: any) => onAdditionalInsuredChange(index, e)}
              />
              {insured.email && !isValidEmail(insured.email) && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  Please enter a valid email address
                </Text>
              )}
              {validateAdditionalInsuredEmail(insured.email, index) && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {validateAdditionalInsuredEmail(insured.email, index)}
                </Text>
              )}
            </Box>
            <FormInput
              section={SECTIONS.APPLICANTS}
              label="Name"
              subLabel={`Additional Insured #${index + 1}`}
              name="name"
              value={insured.name || ''}
              onChange={(e: any) => onAdditionalInsuredChange(index, e)}
              required
            />
            <DateInput
              section={SECTIONS.APPLICANTS}
              label="Date of Birth"
              subLabel={`Additional Insured #${index + 1}`}
              date={getDateFromString(insured.date_of_birth)}
              onChange={(date: Date) => onAdditionalInsuredDateChange(index, date, 'date_of_birth')}
              required
            />
            <FormInput
              section={SECTIONS.APPLICANTS}
              label="Occupation"
              subLabel={`Additional Insured #${index + 1}`}
              name="occupation"
              value={insured.occupation || ''}
              onChange={(e: any) => onAdditionalInsuredChange(index, e)}
              required
            />
            <SelectInput
              section={SECTIONS.APPLICANTS}
              label="Legal Interest"
              subLabel={`Additional Insured #${index + 1}`}
              name="interest"
              value={insured.interest || ''}
              options={INTEREST_OPTIONS}
              onChange={(e: any) => onAdditionalInsuredChange(index, e)}
              required={true}
            />
          </SimpleGrid>
        </Box>
      ))}
    </Card>
  );
};

export default Applicants;
